import dayjs from 'dayjs';

export const RECENT_HISTORY_LIMIT = 20;

const ENTITY_TYPES = [
  'order',
  'intervention',
  'externalRequest',
  'portalRequest',
  'serviceObject',
  'customer',
  'material',
  'warehouse',
  'priceList',
  'adminCompany',
  'adminFeatureFlag',
  'adminSupportTicket'
] as const;

/** Minimal detail metadata; entity payloads and browser back/forward history are not stored. */
export interface RecentHistoryEntry {
  type: (typeof ENTITY_TYPES)[number];
  id: string;
  title: string;
  path: string;
  /** Last successful detail visit, as a Unix timestamp in milliseconds. */
  visitedAt: number;
}

export interface RecentHistoryScope {
  companyId: string;
  userId: string;
}

/**
 * Validate untrusted persisted data and new visits before including them in history.
 * Paths must be application-relative, starting with one slash; external URLs and
 * backslash-based paths are rejected. This does not check route existence or permissions.
 */
const isEntry = (value: unknown): value is RecentHistoryEntry => {
  if (!value || typeof value !== 'object') return false;
  const entry = value as Partial<RecentHistoryEntry>;
  return (
    ENTITY_TYPES.includes(entry.type as RecentHistoryEntry['type']) &&
    typeof entry.id === 'string' &&
    entry.id.trim().length > 0 &&
    typeof entry.title === 'string' &&
    entry.title.trim().length > 0 &&
    typeof entry.path === 'string' &&
    /^\/(?!\/)/.test(entry.path) &&
    !entry.path.includes('\\') &&
    typeof entry.visitedAt === 'number' &&
    Number.isFinite(entry.visitedAt) &&
    entry.visitedAt >= 0
  );
};

/**
 * Drop invalid entries, order newest first and retain at most 20 distinct entities.
 * Identity is the pair (type, id), so equal IDs from different entity types coexist.
 * Sorting before deduplication keeps the latest visit; mapping strips unknown fields.
 * The input array is not mutated. Equal timestamps preserve their input order.
 */
const normalize = (entries: unknown): RecentHistoryEntry[] => {
  if (!Array.isArray(entries)) return [];
  const seen = new Set<string>();
  return entries
    .filter(isEntry)
    .sort((a, b) => b.visitedAt - a.visitedAt)
    .filter((entry) => {
      const identity = JSON.stringify([entry.type, entry.id]);
      if (seen.has(identity)) return false;
      seen.add(identity);
      return true;
    })
    .slice(0, RECENT_HISTORY_LIMIT)
    .map(({ type, id, title, path, visitedAt }) => ({ type, id, title, path, visitedAt }));
};

/**
 * Create a history store for one authenticated user and company.
 * Keep the instance for that scope's lifetime and replace it when either ID changes.
 * The versioned localStorage key encodes each ID separately to avoid delimiter collisions.
 *
 * Reads consult localStorage while it works, allowing subsequent reads to observe
 * writes from other tabs. There is no subscription or transactional cross-tab update.
 * A read/parse/write failure permanently switches this instance to memory, preserving
 * subsequent visits even if persisted data is stale. A new instance can retry storage.
 * All public methods return copies, so callers cannot mutate the store's internal state.
 *
 * @throws When companyId or userId is blank; callers must wait for authenticated identity.
 */
export const createRecentHistory = ({ companyId, userId }: RecentHistoryScope) => {
  if (!companyId.trim() || !userId.trim()) throw new Error('History requires a company and user');
  const key = `mwork.recent.v1:${encodeURIComponent(companyId)}:${encodeURIComponent(userId)}`;
  let memory: RecentHistoryEntry[] = [];
  let storageAvailable = true;

  /**
   * Read and normalize persisted visits, or return the in-memory fallback.
   * A missing key means an empty history. Invalid individual records are discarded;
   * malformed JSON switches to memory without propagating the parsing error.
   */
  const read = (): RecentHistoryEntry[] => {
    if (!storageAvailable) return memory.map((entry) => ({ ...entry }));
    try {
      const raw = window.localStorage.getItem(key);
      memory = raw === null ? [] : normalize(JSON.parse(raw));
    } catch {
      storageAvailable = false;
      // Preserve the in-memory history when persistence cannot be read.
    }
    return memory.map((entry) => ({ ...entry }));
  };

  /** Save an already normalized list in memory first, then persist it if available. */
  const write = (entries: RecentHistoryEntry[]) => {
    memory = entries;
    try {
      if (storageAvailable) window.localStorage.setItem(key, JSON.stringify(entries));
    } catch {
      storageAvailable = false;
      // Browsing remains usable when storage is blocked or full.
    }
    return memory.map((entry) => ({ ...entry }));
  };

  return {
    read,
    /**
     * Record a successfully loaded detail, replacing its previous visit and metadata.
     * The default timestamp places it ahead of older visits; the optional timestamp
     * supports deterministic tests. Invalid input leaves the existing history intact.
     * Callers provide the canonical detail path to deduplicate visits to its subpages.
     */
    record: (entry: Omit<RecentHistoryEntry, 'visitedAt'>, visitedAt = dayjs().valueOf()) => {
      const next = { ...entry, visitedAt };
      if (!isEntry(next)) return read();
      const previous = read().filter((item) => item.type !== entry.type || item.id !== entry.id);
      return write(normalize([next, ...previous]));
    },
    /** Empty this user's/company's history only; persists an empty list when possible. */
    clear: () => write([])
  };
};
